import React, { useEffect } from 'react';
import { AlertCircle, Loader2, Users } from 'lucide-react';

const compact = (n) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return n.toLocaleString();
};

const sizeLabel = (max) => {
  if (max == null) return null;
  if (max < 50_000) return { text: 'Very narrow', tone: 'text-red-300' };
  if (max < 500_000) return { text: 'Specific', tone: 'text-amber-300' };
  if (max < 5_000_000) return { text: 'Balanced', tone: 'text-green-300' };
  return { text: 'Broad', tone: 'text-sky-300' };
};

export const SnapchatAudienceEstimate = ({ snap, formData }) => {
  const { audienceEstimate: est, estimateAudience, selectedAccount, connection } = snap;
  const t = formData.snapTargeting || {};
  const deps = JSON.stringify([
    formData.latitude, formData.longitude, formData.radius, formData.minAge, formData.maxAge, formData.gender,
    t.languages, t.regionIds, t.interestsInclude, t.interestsExclude, t.segmentsInclude, t.segmentsExclude,
    t.osTypes, t.connectionTypes, t.carrierIds, t.autoExpansion, t.smartTargeting, t.regulatedContent,
  ]);

  useEffect(() => {
    if (connection?.connected && selectedAccount?.id) estimateAudience(formData, selectedAccount.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deps, connection?.connected, selectedAccount?.id]);

  if (!connection?.connected) return null;
  const label = sizeLabel(est.max);
  const pct = est.max ? Math.min(100, Math.max(4, Math.log10(est.max) / 8 * 100)) : 0;

  return (
    <div className="p-3 rounded-xl bg-gradient-to-r from-[#FFFC00]/10 to-amber-500/5 border border-[#FFFC00]/30" data-testid="snapchat-audience-estimate">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#FFFC00] text-black flex items-center justify-center"><Users className="h-4 w-4" /></div>
          <div>
            <p className="text-sm font-semibold text-white">Estimated Snapchat audience</p>
            <p className="text-xs text-white/50">Live from Snapchat — updates as you change targeting</p>
          </div>
        </div>
        <div className="text-right">
          {est.status === 'loading' && <Loader2 className="h-4 w-4 animate-spin text-white/60 ml-auto" data-testid="snapchat-audience-loading" />}
          {est.status === 'ready' && (
            <>
              <p className="text-lg font-bold text-white leading-tight" data-testid="snapchat-audience-range">{compact(est.min)} – {compact(est.max)}</p>
              {label && <p className={`text-xs font-medium ${label.tone}`} data-testid="snapchat-audience-label">{label.text}</p>}
            </>
          )}
          {est.status === 'error' && (
            <p className="text-xs text-amber-300 flex items-center gap-1 max-w-[260px]" data-testid="snapchat-audience-error"><AlertCircle className="h-3 w-3 flex-shrink-0" /> {est.error}</p>
          )}
        </div>
      </div>
      {est.status === 'ready' && (
        <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-red-400 via-green-400 to-sky-400 transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
};
